import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { getHolesByCourse, getStrokesForHole, calculateStablefordPoints } from './courseData';
import { getWeatherIcon } from './weatherService';

function MyRounds({ onClose, currentUserId, onEditRound }) {
  const [myRounds, setMyRounds] = useState([]);
  const [selectedRound, setSelectedRound] = useState(null);
  const [filter, setFilter] = useState('all'); // all, pending, approved, disputed
  const [users, setUsers] = useState({});

  useEffect(() => {
    const q = query(
      collection(db, 'rounds'),
      where('userId', '==', currentUserId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const rounds = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setMyRounds(rounds);
    });

    return unsubscribe;
  }, [currentUserId]);

  // Load all users for display names
  useEffect(() => {
    const loadUsers = async () => {
      const usersQuery = query(collection(db, 'users'));
      const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
        const usersMap = {};
        snapshot.docs.forEach(doc => {
          usersMap[doc.id] = doc.data();
        });
        setUsers(usersMap);
      });
      return unsubscribe;
    };

    loadUsers();
  }, []);

  const handleViewRound = (round) => {
    setSelectedRound(round);
  };

  const handleEditDisputed = (round) => {
  onEditRound(round);
};

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: '⏳ Pending', color: '#ed8936' },
      approved: { text: '✅ Approved', color: '#48bb78' },
      disputed: { text: '❌ Disputed', color: '#e53e3e' }
    };
    return badges[status] || badges.pending;
  };

  const getUserName = (userId) => {
    return users[userId]?.name || 'Unknown';
  };

  const filteredRounds = myRounds.filter(round => {
    if (filter === 'all') return true;
    return round.status === filter;
  });

  // Round Details View
  if (selectedRound) {
    const holes = getHolesByCourse(selectedRound.course);
    const statusBadge = getStatusBadge(selectedRound.status);
    const markerName = getUserName(selectedRound.markerId);
    const flightMemberNames = (selectedRound.flightMembers || []).map(id => getUserName(id));
    
    return (
      <div style={styles.overlay}>
        <div style={styles.cardWide}>
          <div style={styles.header}>
            <h2 style={styles.title}>Round Details</h2>
            <button onClick={() => setSelectedRound(null)} style={styles.closeButton}>✕</button>
          </div>

          <div style={styles.scrollContent}>
            <div style={{...styles.statusBanner, backgroundColor: statusBadge.color}}>
              <span style={styles.statusText}>{statusBadge.text}</span>
              {selectedRound.status === 'disputed' && selectedRound.disputeReason && (
                <span style={styles.disputeReason}>Reason: {selectedRound.disputeReason}</span>
              )}
            </div>

            <div style={styles.playerInfo}>
              <div style={styles.playerDetail}>
                <span style={styles.label}>Event:</span>
                <span style={styles.value}>{selectedRound.eventName || 'Unknown Event'}</span>
              </div>
              <div style={styles.playerDetail}>
                <span style={styles.label}>Course:</span>
                <span style={styles.value}>{selectedRound.course}</span>
              </div>
              <div style={styles.playerDetail}>
                <span style={styles.label}>Date:</span>
                <span style={styles.value}>
                  {new Date(selectedRound.date).toLocaleDateString()}
                </span>
              </div>
              <div style={styles.playerDetail}>
                <span style={styles.label}>Handicap:</span>
                <span style={styles.value}>
                  {selectedRound.handicap} (Course: {selectedRound.courseHandicap})
                </span>
              </div>
            </div>

            {/* Flight Members & Marker Section */}
            <div style={styles.peopleSection}>
              <div style={styles.peopleCard}>
                <h4 style={styles.peopleTitle}>👥 Flight Members</h4>
                {flightMemberNames.length > 0 ? (
                  <div style={styles.peopleList}>
                    {flightMemberNames.map((name, index) => (
                      <div key={index} style={styles.personItem}>
                        <span style={styles.personDot}>•</span>
                        <span style={styles.personName}>{name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={styles.emptyText}>No flight members recorded</p>
                )}
              </div>

              <div style={styles.peopleCard}>
                <h4 style={styles.peopleTitle}>✅ Marker</h4>
                <div style={styles.markerBox}>
                  <span style={styles.markerName}>{markerName}</span>
                </div>
              </div>
            </div>

            {selectedRound.weather && (
              <div style={styles.weatherSection}>
                <h4 style={styles.weatherTitle}>☀️ Weather Conditions</h4>
                <div style={styles.weatherGrid}>
                  <div style={styles.weatherItem}>
                    <span style={styles.weatherIcon}>{getWeatherIcon(selectedRound.weather.condition)}</span>
                    <span style={styles.weatherValue}>{selectedRound.weather.temp}°C</span>
                    <span style={styles.weatherLabel}>{selectedRound.weather.description}</span>
                  </div>
                  <div style={styles.weatherItem}>
                    <span style={styles.weatherIcon}>💨</span>
                    <span style={styles.weatherValue}>{selectedRound.weather.windSpeed} km/h</span>
                    <span style={styles.weatherLabel}>Wind</span>
                  </div>
                  <div style={styles.weatherItem}>
                    <span style={styles.weatherIcon}>💧</span>
                    <span style={styles.weatherValue}>{selectedRound.weather.humidity}%</span>
                    <span style={styles.weatherLabel}>Humidity</span>
                  </div>
                </div>
              </div>
            )}

            <div style={styles.summary}>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Gross Score</div>
                <div style={styles.summaryValue}>{selectedRound.totalGross}</div>
              </div>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Stableford Points</div>
                <div style={styles.summaryValue}>{selectedRound.totalStableford}</div>
              </div>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Total Putts</div>
                <div style={styles.summaryValue}>{selectedRound.totalPutts}</div>
              </div>
            </div>

            <h3 style={styles.sectionTitle}>📋 Hole-by-Hole Scorecard</h3>
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Hole</th>
                    <th style={styles.th}>Par</th>
                    <th style={styles.th}>SI</th>
                    <th style={styles.th}>Strokes</th>
                    <th style={styles.th}>Putts</th>
                    <th style={styles.th}>Net</th>
                    <th style={styles.th}>Points</th>
                  </tr>
                </thead>
                <tbody>
                  {holes.map(hole => {
                    const data = selectedRound.holeData[hole.number] || {};
                    const strokesReceived = getStrokesForHole(hole.strokeIndex, selectedRound.courseHandicap, selectedRound.course);
                    const netScore = data.strokes ? data.strokes - strokesReceived : '-';
                    const points = data.strokes ? calculateStablefordPoints(data.strokes, hole.par, strokesReceived) : '-';
                    
                    return (
                      <tr key={hole.number} style={styles.tr}>
                        <td style={{...styles.td, fontWeight: 'bold'}}>{hole.number}</td>
                        <td style={styles.td}>{hole.par}</td>
                        <td style={styles.td}>{hole.strokeIndex}</td>
                        <td style={{...styles.td, fontWeight: 'bold', fontSize: '16px'}}>{data.strokes || '-'}</td>
                        <td style={styles.td}>{data.putts || '-'}</td>
                        <td style={styles.td}>{netScore}</td>
                        <td style={{...styles.td, fontWeight: 'bold', color: points > 2 ? '#48bb78' : points < 2 ? '#e53e3e' : '#3182ce', fontSize: '16px'}}>
                          {points}
                        </td>
                      </tr>
                    );
                  })}
                  <tr style={styles.totalRow}>
                    <td style={styles.totalCell} colSpan="3"><strong>TOTAL</strong></td>
                    <td style={{...styles.totalCell, fontSize: '18px'}}><strong>{selectedRound.totalGross}</strong></td>
                    <td style={styles.totalCell}><strong>{selectedRound.totalPutts}</strong></td>
                    <td style={styles.totalCell}>-</td>
                    <td style={{...styles.totalCell, color: '#3182ce', fontSize: '18px'}}><strong>{selectedRound.totalStableford}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>

            {selectedRound.status === 'disputed' && (
              <div style={styles.disputedNote}>
                <p><strong>⚠️ This round was disputed by your marker</strong></p>
                <p><strong>Reason:</strong> {selectedRound.disputeReason}</p>
                <p style={styles.disputedInstructions}>
                  You can edit and resubmit this round. Click "Edit & Resubmit" below to fix any mistakes.
                </p>
                <button 
                  onClick={() => handleEditDisputed(selectedRound)} 
                  style={styles.editButton}
                >
                  ✏️ Edit & Resubmit Round
                </button>
              </div>
            )}
          </div>

          <div style={styles.actions}>
            <button onClick={() => setSelectedRound(null)} style={styles.backButton}>
              ← Back to My Rounds
            </button>
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>My Rounds</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.filterBar}>
          <button 
            onClick={() => setFilter('all')} 
            style={{...styles.filterButton, ...(filter === 'all' ? styles.filterButtonActive : {})}}
          >
            All ({myRounds.length})
          </button>
          <button 
            onClick={() => setFilter('pending')} 
            style={{...styles.filterButton, ...(filter === 'pending' ? styles.filterButtonActive : {})}}
          >
            ⏳ Pending ({myRounds.filter(r => r.status === 'pending').length})
          </button>
          <button 
            onClick={() => setFilter('approved')} 
            style={{...styles.filterButton, ...(filter === 'approved' ? styles.filterButtonActive : {})}}
          >
            ✅ Approved ({myRounds.filter(r => r.status === 'approved').length})
          </button>
          <button 
            onClick={() => setFilter('disputed')} 
            style={{...styles.filterButton, ...(filter === 'disputed' ? styles.filterButtonActive : {})}}
          >
            ❌ Disputed ({myRounds.filter(r => r.status === 'disputed').length})
          </button>
        </div>

        {filteredRounds.length === 0 ? (
          <div style={styles.emptyState}>
            <p>📊 No rounds found</p>
            <p style={styles.emptySubtext}>
              {filter === 'all' 
                ? "Submit your first round to see it here!" 
                : `No ${filter} rounds yet.`}
            </p>
          </div>
        ) : (
          <div style={styles.roundsList}>
            {filteredRounds.map(round => {
              const statusBadge = getStatusBadge(round.status);
              return (
                <div key={round.id} style={styles.roundCard} onClick={() => handleViewRound(round)}>
                  <div style={styles.roundHeader}>
                    <div>
                      <h3 style={styles.roundCourse}>
                        {round.course}
                        {round.isFinale && <span style={styles.finaleBadge}>🏆 Finale</span>}
                      </h3>
                      <span style={styles.roundDate}>
                        {new Date(round.date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    <span style={{...styles.statusBadgeSmall, backgroundColor: statusBadge.color}}>
                      {statusBadge.text}
                    </span>
                  </div>
                  <div style={styles.roundStats}>
                    <div style={styles.stat}>
                      <span style={styles.statLabel}>Score</span>
                      <span style={styles.statValue}>{round.isFinale ? round.finaleNetGross : round.totalGross}</span>
                    </div>
                    <div style={styles.stat}>
                      <span style={styles.statLabel}>Points</span>
                      <span style={{...styles.statValue, color: '#3182ce'}}>{round.totalStableford}</span>
                    </div>
                    <div style={styles.stat}>
                      <span style={styles.statLabel}>Putts</span>
                      <span style={styles.statValue}>{round.totalPutts}</span>
                    </div>
                    <div style={styles.stat}>
                      <span style={styles.statLabel}>HCP</span>
                      <span style={styles.statValue}>{round.handicap}</span>
                    </div>
                  </div>
                  {round.status === 'disputed' && (
                    <div style={styles.disputedBanner}>
                      <span>⚠️ Disputed - Click to edit & resubmit</span>
                    </div>
                  )}
                  <div style={styles.viewButton}>
                    View Details →
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    overflowY: 'auto',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  },
  card: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '700px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp 0.25s ease'
  },
  cardWide: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 30px',
    borderBottom: '1px solid #2d2d2d',
    flexShrink: 0
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#ffffff',
    margin: 0,
    letterSpacing: '-0.01em'
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: '5px 10px',
    transition: 'all 0.3s ease'
  },
  scrollContent: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px 30px'
  },
  filterBar: {
    display: 'flex',
    gap: '10px',
    padding: '15px 20px',
    borderBottom: '1px solid #2d2d2d',
    overflowX: 'auto',
    flexShrink: 0
  },
  filterButton: {
    padding: '8px 16px',
    backgroundColor: '#0f0f0f',
    border: '1px solid #2d2d2d',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#9ca3af',
    whiteSpace: 'nowrap',
    transition: 'all 0.3s ease'
  },
  filterButtonActive: {
    backgroundColor: '#d4af37',
    borderColor: '#d4af37',
    color: '#0f0f0f',
    fontWeight: '600'
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#6b7280',
    fontWeight: '400'
  },
  emptySubtext: {
    fontSize: '14px',
    marginTop: '10px',
    color: '#6b7280'
  },
  roundsList: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1
  },
  roundCard: {
    backgroundColor: '#0f0f0f',
    border: '1px solid #2d2d2d',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '15px',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  roundHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px'
  },
  roundCourse: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#ffffff',
    margin: '0 0 5px 0',
    letterSpacing: '-0.01em'
  },
  finaleBadge: {
    marginLeft: '10px',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '700',
    color: '#0f0f0f',
    backgroundColor: '#d4af37',
    verticalAlign: 'middle'
  },
  roundDate: {
    fontSize: '13px',
    color: '#9ca3af',
    fontWeight: '400'
  },
  statusBadgeSmall: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '700',
    color: '#0f0f0f'
  },
  roundStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '15px',
    marginBottom: '10px'
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  statLabel: {
    fontSize: '10px',
    color: '#6b7280',
    marginBottom: '3px',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.05em'
  },
  statValue: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#d4af37',
    letterSpacing: '-0.02em'
  },
  disputedBanner: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #ef4444',
    borderRadius: '6px',
    padding: '8px 12px',
    marginTop: '10px',
    marginBottom: '5px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#ef4444',
    fontWeight: '600'
  },
  viewButton: {
    color: '#d4af37',
    fontSize: '14px',
    fontWeight: '600',
    textAlign: 'right'
  },
  statusBanner: {
    padding: '15px 20px',
    borderRadius: '8px',
    marginBottom: '20px',
    color: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  statusText: {
    fontSize: '18px',
    fontWeight: '600',
    letterSpacing: '-0.01em'
  },
  disputeReason: {
    fontSize: '14px',
    opacity: 0.9,
    fontWeight: '400'
  },
  playerInfo: {
    backgroundColor: '#0f0f0f',
    padding: '20px',
    borderRadius: '8px',
    border: '1px solid #2d2d2d',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '20px'
  },
  playerDetail: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  label: {
    fontSize: '11px',
    color: '#6b7280',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.05em'
  },
  value: {
    fontSize: '16px',
    color: '#ffffff',
    fontWeight: '600'
  },
  peopleSection: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '20px'
  },
  peopleCard: {
    backgroundColor: '#0f0f0f',
    padding: '20px',
    borderRadius: '8px',
    border: '1px solid #2d2d2d'
  },
  peopleTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    marginTop: 0,
    marginBottom: '15px',
    letterSpacing: '-0.01em'
  },
  peopleList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  personItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  personDot: {
    color: '#d4af37',
    fontSize: '18px',
    fontWeight: 'bold'
  },
  personName: {
    fontSize: '15px',
    color: '#9ca3af',
    fontWeight: '500'
  },
  markerBox: {
    backgroundColor: '#0f0f0f',
    border: '1px solid #10b981',
    borderRadius: '8px',
    padding: '15px',
    textAlign: 'center'
  },
  markerName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#10b981',
    letterSpacing: '-0.01em'
  },
  emptyText: {
    fontSize: '14px',
    color: '#6b7280',
    fontStyle: 'italic',
    margin: 0,
    fontWeight: '400'
  },
  summary: {
    display: 'flex',
    justifyContent: 'space-around',
    marginBottom: '30px'
  },
  summaryItem: {
    textAlign: 'center'
  },
  summaryLabel: {
    fontSize: '11px',
    color: '#6b7280',
    marginBottom: '5px',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.05em'
  },
  summaryValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#d4af37',
    letterSpacing: '-0.02em'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '15px',
    letterSpacing: '-0.01em'
  },
  tableContainer: {
    overflowX: 'auto',
    marginBottom: '20px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  th: {
    backgroundColor: '#0f0f0f',
    color: '#d4af37',
    padding: '12px 8px',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  tr: {
    borderBottom: '1px solid #2d2d2d'
  },
  td: {
    padding: '12px 8px',
    textAlign: 'center',
    color: '#9ca3af',
    fontWeight: '500'
  },
  totalRow: {
    backgroundColor: '#0f0f0f',
    borderTop: '2px solid #d4af37'
  },
  totalCell: {
    padding: '12px 8px',
    textAlign: 'center',
    fontWeight: '700',
    color: '#d4af37'
  },
  disputedNote: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #ef4444',
    borderRadius: '8px',
    padding: '20px',
    marginTop: '20px'
  },
  disputedInstructions: {
    marginTop: '10px',
    marginBottom: '15px',
    color: '#9ca3af',
    fontWeight: '400'
  },
  editButton: {
    width: '100%',
    padding: '15px',
    backgroundColor: '#f59e0b',
    color: '#0f0f0f',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  actions: {
    display: 'flex',
    gap: '15px',
    padding: '20px 30px',
    borderTop: '1px solid #2d2d2d',
    flexShrink: 0
  },
  backButton: {
    flex: 1,
    padding: '15px',
    backgroundColor: '#2d2d2d',
    color: '#9ca3af',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  },
  weatherSection: {
    backgroundColor: '#0f0f0f',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px',
    border: '1px solid #2d2d2d'
  },
  weatherTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#ffffff',
    marginTop: 0,
    marginBottom: '15px',
    letterSpacing: '-0.01em'
  },
  weatherGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '15px'
  },
  weatherItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '5px'
  },
  weatherIcon: {
    fontSize: '32px'
  },
  weatherValue: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#d4af37'
  },
  weatherLabel: {
    fontSize: '12px',
    color: '#9ca3af',
    fontWeight: '500'
  }
};

export default MyRounds;